/** Physiotherapist CareHub — dossier fields, nav, and availability defaults. */

import { resolveNpidCode, formatNcidForDisplay } from "../utils/npidClient";

export const PHYSIO_CLINICAL_SPECIALIZATIONS = [
  "Orthopedic Rehab",
  "Neurological Rehab",
  "Post-Stroke Care",
  "Geriatric Care",
  "Sports Injury Rehab",
  "Pediatric Rehab",
  "Cardiorespiratory PT",
  "Spinal Manual Therapy",
  "Dry Needling",
];

export const PHYSIO_SPOKEN_LANGUAGES = [
  "English",
  "Hindi",
  "Punjabi",
  "Malayalam",
  "Tamil",
  "Bengali",
  "Gujarati",
  "Kannada",
  "Telugu",
  "Marathi",
];

export const PHYSIO_CERTIFICATE_TYPES = [
  {
    id: "govtIdentity",
    label: "1. Government Identity (Aadhaar/Passport)",
    required: true,
  },
  {
    id: "iapMembership",
    label: "2. Licensing Board Certificate (IAP Membership)",
    required: true,
  },
  {
    id: "degreeCert",
    label: "3. Degree Certifications (BPT / MPT)",
    required: true,
  },
  {
    id: "experienceCert",
    label: "4. Experience Certificate Hospital letter",
    required: false,
  },
];

export const PHYSIO_CONSULTATION_FORMAT = "Home Visit Only (NEMA CareHub Platform Standard)";

export const PHYSIO_SERVICE_RADIUS_MAX_KM = 25;
export const PHYSIO_SERVICE_RADIUS_DEFAULT_KM = 12;
export const PHYSIO_TRAVEL_BUFFER_DISPLAY_MIN = 30;
export const PHYSIO_STERILE_BUFFER_DEFAULT_MIN = 15;
export const PHYSIO_DAILY_VISIT_CAP_DEFAULT = 4;
export const PHYSIO_DAILY_VISIT_CAP_MAX = 8;
export const PHYSIO_MAX_DAILY_HOURS_DEFAULT = 8;
export const PHYSIO_MAX_DAILY_HOURS_MAX = 12;
export const PHYSIO_MIN_REST_HOURS_DEFAULT = 1;
export const PHYSIO_MIN_REST_HOURS_MAX = 4;
export const PHYSIO_DEFAULT_SLOT_START = "09:00";
export const PHYSIO_DEFAULT_SLOT_END = "18:00";
export const PHYSIO_LEAVE_REASON = "Leave";
export const PHYSIO_BLOCK_REASON_PREFIX = "BLOCK:";
export const PHYSIO_BIO_MIN_CHARS = 30;
export const PHYSIO_PROFILE_LOCK_PERCENT = 90;

export const PHYSIO_WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export const PHYSIO_SUPPORT_CATEGORIES = [
  "Credits",
  "Subscription",
  "GST / Invoices",
  "Bookings",
  "Profile / Compliance",
  "Technical",
  "Other",
];

export const PHYSIO_SUPPORT_PRIORITIES = ["Low", "Medium", "High"];

/** Sidebar for physiotherapists — same CareHub menus as care attendants. */
export const PHYSIO_SIDEBAR_ITEMS = [
  { to: "/caregiver", label: "Dashboard", end: true, icon: "grid" },
  { to: "/caregiver/start-end-service", label: "Start / End Service", icon: "clock" },
  { to: "/caregiver/my-bookings", label: "My Bookings", icon: "list" },
  { to: "/caregiver/subscription", label: "Subscription & Credits", icon: "card" },
  { to: "/caregiver/payments", label: "Payments", icon: "rupee" },
  { to: "/caregiver/my-schedule", label: "My Schedule", icon: "calendar" },
  { to: "/caregiver/availability", label: "Availability Calendar", icon: "calendar" },
  { to: "/caregiver/care-notes", label: "Care Notes", icon: "document" },
  { to: "/caregiver/earnings", label: "Earnings", icon: "wallet" },
  {
    to: "/caregiver/complete-profile",
    label: "Profile",
    icon: "user",
    badgeKey: "completionPercent",
  },
  { to: "/caregiver/pricing", label: "Pricing & Packages", icon: "rupee" },
  { to: "/caregiver/support", label: "Help & Support", icon: "help" },
];

export const PHYSIO_TOP_NAV_ITEMS = [
  { to: "/caregiver", label: "Caregivers", end: true, icon: "grid" },
  { to: "/caregiver/start-end-service", label: "Start / End Service", icon: "clock" },
  { to: "/caregiver/my-bookings", label: "My Bookings", icon: "list" },
  { to: "/caregiver/earnings", label: "Earnings", icon: "wallet" },
  { to: "/caregiver/complete-profile", label: "Profile", icon: "user" },
];

export function createEmptyPhysioDossier() {
  return {
    registrationNo: "",
    clinicalSpecializations: [],
    spokenLanguages: [],
    clinicalBio: "",
    consultationFormat: "HOME_VISIT",
    serviceRadiusKm: PHYSIO_SERVICE_RADIUS_DEFAULT_KM,
    streetAddress: "",
    pincode: "",
    panNumber: "",
    gstin: "",
    upiId: "",
    bank: {
      accountHolderName: "",
      accountNumber: "",
      ifsc: "",
      bankName: "",
    },
    certificates: {
      govtIdentity: { uploaded: false, fileName: "", fileUrl: "", storageKey: "" },
      iapMembership: { uploaded: false, fileName: "", fileUrl: "", storageKey: "" },
      degreeCert: { uploaded: false, fileName: "", fileUrl: "", storageKey: "" },
      experienceCert: { uploaded: false, fileName: "", fileUrl: "", storageKey: "" },
    },
    authorizations: {
      complianceVerify: false,
      marketplaceTerms: false,
    },
    mobileVerified: false,
    submittedForReview: false,
    submittedAt: null,
  };
}

export function mergePhysioDossier(raw) {
  const base = createEmptyPhysioDossier();
  if (!raw || typeof raw !== "object") return base;
  return {
    ...base,
    ...raw,
    clinicalSpecializations: Array.isArray(raw.clinicalSpecializations)
      ? raw.clinicalSpecializations
      : base.clinicalSpecializations,
    spokenLanguages: Array.isArray(raw.spokenLanguages)
      ? raw.spokenLanguages
      : base.spokenLanguages,
    bank: { ...base.bank, ...(raw.bank || {}) },
    certificates: {
      govtIdentity: { ...base.certificates.govtIdentity, ...(raw.certificates?.govtIdentity || {}) },
      iapMembership: {
        ...base.certificates.iapMembership,
        ...(raw.certificates?.iapMembership || {}),
      },
      degreeCert: { ...base.certificates.degreeCert, ...(raw.certificates?.degreeCert || {}) },
      experienceCert: {
        ...base.certificates.experienceCert,
        ...(raw.certificates?.experienceCert || {}),
      },
    },
    authorizations: { ...base.authorizations, ...(raw.authorizations || {}) },
  };
}

/** Rough dossier completion % — profile fields count when already collected elsewhere. */
export function calculatePhysioDossierPercent(dossier, basic = {}, profile = {}) {
  const d = mergePhysioDossier(dossier);
  const professional = profile.professional || {};
  const docs = profile.profileExtended?.documents || {};
  const languages =
    (Array.isArray(d.spokenLanguages) && d.spokenLanguages.length
      ? d.spokenLanguages
      : professional.languages) || [];
  const langProf = profile.profileExtended?.languageProficiency || [];
  const hasLanguages =
    languages.length >= 1 ||
    langProf.some((row) => String(row?.language || "").trim());
  const bio = String(d.clinicalBio || professional.bio || "").trim();
  const address = String(d.streetAddress || basic.address || "").trim();
  const hasGovtId = Boolean(
    d.certificates?.govtIdentity?.uploaded ||
      d.certificates?.govtIdentity?.fileUrl ||
      docs.aadhaarFront?.fileUrl ||
      docs.aadhaarFront?.uploaded
  );
  const hasIap = Boolean(
    d.certificates?.iapMembership?.uploaded ||
      d.certificates?.iapMembership?.fileUrl ||
      docs.iap?.fileUrl ||
      docs.iap?.uploaded
  );
  const hasDegree = Boolean(
    d.certificates?.degreeCert?.uploaded ||
      d.certificates?.degreeCert?.fileUrl ||
      docs.degree?.fileUrl ||
      docs.degree?.uploaded
  );

  const checks = [
    Boolean(String(basic.mobile || "").trim()),
    Boolean(String(basic.fullName || "").trim().length >= 3),
    Boolean(String(basic.email || "").trim()),
    Boolean(String(basic.gender || "").trim()),
    Boolean(String(d.registrationNo || "").trim().length >= 3),
    (d.clinicalSpecializations || []).length >= 1,
    hasLanguages,
    bio.length >= PHYSIO_BIO_MIN_CHARS,
    Number(d.serviceRadiusKm) >= 1 && Number(d.serviceRadiusKm) <= PHYSIO_SERVICE_RADIUS_MAX_KM,
    Boolean(address),
    /^\d{6}$/.test(String(d.pincode || "").trim()),
    /^[A-Z]{5}[0-9]{4}[A-Z]$/i.test(String(d.panNumber || "").trim()),
    Boolean(String(d.upiId || "").trim()),
    Boolean(String(d.bank?.accountHolderName || "").trim()),
    Boolean(String(d.bank?.accountNumber || "").trim()),
    Boolean(String(d.bank?.ifsc || "").trim()),
    Boolean(String(d.bank?.bankName || "").trim()),
    hasGovtId,
    hasIap,
    hasDegree,
    Boolean(d.authorizations?.complianceVerify),
    Boolean(d.authorizations?.marketplaceTerms),
  ];
  const done = checks.filter(Boolean).length;
  return Math.round((done / checks.length) * 100);
}

export function formatPhysioProviderId(npid, caregiverId) {
  const code = resolveNpidCode(npid).toUpperCase();
  if (code) {
    const m = code.match(/^(?:NCID|NPID)-(\d{2})-PT-(\d{5})$/);
    if (m) return `NEMA-PT-${m[2]}`;
    if (code.includes("PT")) return code.replace(/^(?:NCID|NPID)-/, "NEMA-");
    return formatNcidForDisplay(code);
  }
  // Do not invent provider IDs from caregiver row id — wait for admin-issued NCID.
  void caregiverId;
  return "";
}
